<template>
  <article class="relative overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-ink-sm transition-colors hover:border-ring/40">
    <!-- Крупная карточка маршрута (каталог «Списком», «Маршруты рядом» в поиске):
         картинка карты с линией на всю карточку, название, сложность, цифры и
         расстояние лежат поверх неё на градиенте от цвета карточки. Вся карточка - ссылка; «сохранить» - отдельная кнопка в углу
         картинки (соседний элемент, а не вложенный: её нажатие не открывает маршрут). -->
    <a
      :href="route.slug ? `/route/${route.slug}` : `/map?route=${route.id}`"
      class="group relative block outline-none focus-visible:ring-2 focus-visible:ring-ring"
      @click.prevent="$emit('open', route.id)"
    >
      <RoutePreviewImage :route-id="route.id" :line="route.preview_line ?? null" :color="lineColor" />
      <!-- Информация поверх картинки: плавный градиент от цвета карточки снизу,
           чтобы текст читался, а карта оставалась видна. -->
      <div
        data-card-overlay
        class="absolute inset-x-0 bottom-0 flex flex-col gap-1.5 bg-gradient-to-t from-card from-55% via-card/80 to-transparent px-3 pb-3 pt-16 transition-colors"
      >
        <div class="flex items-start justify-between gap-2">
          <h3 class="text-[16px] font-semibold leading-tight line-clamp-2">{{ routeTitle(route.name) }} <VerifiedMark :verified="!community" :size="16" class="-mt-0.5" /></h3>
          <GradeBadge v-if="route.technical_grade" :grade="route.technical_grade" class="shrink-0" />
        </div>
        <DataPlate :columns="3" :items="plate" />
        <p v-if="distanceKm != null || shape !== null || route.rating_count" class="flex flex-wrap gap-x-3 gap-y-0.5 text-[12.5px] text-muted-foreground">
          <span v-if="route.rating_count && route.rating != null" data-rating class="num inline-flex items-center gap-1 font-semibold text-foreground">
            <RatingStar :rating="Number(route.rating)" />{{ Number(route.rating).toFixed(1) }}
            <span class="font-normal text-muted-foreground">({{ route.rating_count }})</span>
          </span>
          <span v-if="distanceKm != null" data-distance class="num inline-flex items-center gap-1">
            <Navigation class="h-3.5 w-3.5" aria-hidden="true" />
            <span class="sr-only">{{ $t('routes.distanceLabel') }}</span>
            {{ distanceText }} {{ $t('routes.distanceFromYou') }}
          </span>
          <span v-if="shape !== null">{{ shape ? $t('routes.shape.loop') : $t('routes.shape.linear') }}</span>
        </p>
      </div>
    </a>
    <!-- Угловое действие: «сохранить» (по умолчанию) или своё (слот corner - «Править» у своих маршрутов). -->
    <slot name="corner">
    <button
      v-if="!hideSave"
      type="button"
      data-save
      class="absolute top-2.5 right-2.5 flex h-9 w-9 items-center justify-center rounded-md border border-border shadow-ink-sm"
      :class="saved ? 'bg-forest text-white' : 'bg-sheet text-foreground'"
      :aria-pressed="saved"
      :aria-label="saved ? $t('routeDetails.actions.saved') : $t('routeDetails.actions.save')"
      @click.stop="authed ? $emit('toggle-save', route.id) : $emit('login-to-save', route.id)"
    >
      <Bookmark class="h-4 w-4" :class="saved && 'fill-current'" aria-hidden="true" />
    </button>
    </slot>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Bookmark, Navigation } from "lucide-vue-next";
import RatingStar from "./RatingStar.vue";
import type { RouteMapItem } from "../../types/routes";
import { formatHikingDuration } from "../../utils/formatDuration";
import { routeTitle } from "../../utils/routeName";
import { isLoop, routeSource } from "../../utils/routeFilters";
import { gradeAccentColor } from "../../utils/routeGrade";
import GradeBadge from "../ui/GradeBadge.vue";
import VerifiedMark from "../ui/VerifiedMark.vue";
import DataPlate from "../ui/DataPlate.vue";
import RoutePreviewImage from "./RoutePreviewImage.vue";

// route.preview_line - сырые пары [lon, lat] из GET /routes (RouteMapItem), как
// их ждёт MapLibre. Не путать с RouteHit.line из search/apiProvider - там
// [lat, lon]: из поиска передавать маршрут из store/routes, не хит.
const props = defineProps<{ route: RouteMapItem; distanceKm?: number | null; saved: boolean; authed: boolean; hideSave?: boolean }>();
// login-to-save - гость нажал «сохранить»: родитель откроет вход и сохранит после него.
defineEmits<{ (e: "open" | "toggle-save" | "login-to-save", id: number): void }>();

// #1f7a5c ~= hsl(158 38% 36%), --primary дизайн-системы: MapLibre не читает
// CSS-переменные, нужен литерал (тот же, что в MapView.vue).
const PRIMARY_HEX = "#1f7a5c";

const { t } = useI18n();
const lineColor = computed(() => (props.route.technical_grade ? gradeAccentColor(props.route.technical_grade) : PRIMARY_HEX));
const shape = computed(() => isLoop(props.route));
const community = computed(() => routeSource(props.route) === "community");
const distanceText = computed(() =>
  props.distanceKm != null ? `${props.distanceKm.toFixed(1).replace(".", t("units.decimal"))} ${t("units.km")}` : ""
);
const plate = computed(() => [
  { label: t("routeDetails.plate.distance"), value: props.route.distance_km != null ? props.route.distance_km.toFixed(1) : "—", unit: t("units.km") },
  { label: t("routeDetails.plate.gain"), value: props.route.elevation_gain_m != null ? `+${Math.round(props.route.elevation_gain_m)}` : "—", unit: t("units.m") },
  { label: t("routeDetails.plate.time"), value: formatHikingDuration(props.route.estimated_duration_minutes, t("units.h"), t("units.decimal")) || "—" },
]);
</script>
